import { Box, chakra, Image, ImageProps } from '@chakra-ui/react';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';
import { isSheenReplayAllowed } from 'src/features/MP/utils/sheenReplay';

const MotionBox = chakra(motion.div);
const MotionSheen = chakra(motion.div);

const ENTRANCE_DURATION = 0.8;
const SHEEN_DELAY = ENTRANCE_DURATION + 0.2;
const SHEEN_DURATION = 1.4;

type GlassSheenImageProps = ImageProps & {
  src: string;
};

// Fades and rises the image into place on load, then sweeps a diagonal highlight across it.
// The sweep replays when the pointer enters the image, with a short cooldown between replays.
// The highlight is masked by the image's own alpha, so only the artwork catches the light.
export const GlassSheenImage: React.FC<GlassSheenImageProps> = ({
  src,
  ...imageProps
}) => {
  const shouldReduceMotion = useReducedMotion();
  const sheenControls = useAnimationControls();
  const lastSheenAt = useRef(0);

  const playSheen = useCallback(
    (delay: number) => {
      lastSheenAt.current = Date.now();
      sheenControls.set({ x: '-100%', skewX: -20 });
      sheenControls.start({
        x: '260%',
        skewX: -20,
        transition: { duration: SHEEN_DURATION, delay, ease: 'easeInOut' },
      });
    },
    [sheenControls],
  );

  useEffect(() => {
    if (!shouldReduceMotion) playSheen(SHEEN_DELAY);
  }, [shouldReduceMotion, playSheen]);

  const handleMouseEnter = () => {
    if (shouldReduceMotion) return;
    if (!isSheenReplayAllowed(lastSheenAt.current, Date.now())) return;
    playSheen(0);
  };

  // Timing lives in variants so framer-motion's transition never reaches Chakra's style props.
  const entranceVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: ENTRANCE_DURATION, ease: [0.16, 1, 0.3, 1] },
    },
  };

  // Quote the URL and drop characters that could break out of url("...").
  const maskUrl = `url("${src.replace(/["\\\r\n]/g, '')}")`;

  const maskSx = {
    maskImage: maskUrl,
    WebkitMaskImage: maskUrl,
    maskSize: '100% 100%',
    WebkitMaskSize: '100% 100%',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
  };

  return (
    <MotionBox
      position="relative"
      variants={entranceVariants}
      initial={shouldReduceMotion ? false : 'hidden'}
      animate="visible"
      onMouseEnter={handleMouseEnter}>
      <Image src={src} {...imageProps} />
      {!shouldReduceMotion && (
        <Box
          aria-hidden
          position="absolute"
          inset={0}
          overflow="hidden"
          pointerEvents="none"
          sx={maskSx}>
          <MotionSheen
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            w="40%"
            bgGradient="linear(to-r, transparent, rgba(255, 255, 255, 0.35), transparent)"
            initial={{ x: '-100%', skewX: -20 }}
            animate={sheenControls}
          />
        </Box>
      )}
    </MotionBox>
  );
};
